import { intervalToDuration } from 'date-fns';
import { t } from 'i18next';

export function secondsToString(seconds, isFullIndicator = true) {
  const numdays = Math.floor(seconds / 86400);
  const numhours = Math.floor((seconds % 86400) / 3600);
  const numminutes = Math.floor(((seconds % 86400) % 3600) / 60);
  const numseconds = Math.floor(((seconds % 86400) % 3600) % 60);

  const minutes = `${numminutes} ${numminutes > 1 ? t('general.minutes') : t('general.minute')}`;
  if (numdays === 0 && numhours === 0 && numminutes === 0) {
    return `${numseconds} ${t('general.seconds')}`;
  } else if (numdays === 0 && numhours === 0) {
    if (isFullIndicator) return `${minutes} ${numseconds} ${t('general.seconds')}`;
    return `${numminutes}m ${numseconds}s`;
  } else if (numdays === 0) {
    if (isFullIndicator) return `${numhours} ${t('general.hours')} ${minutes} ${numseconds} ${t('general.seconds')}`;
    return `${numhours}h ${numminutes}m ${numseconds}s`;
  }
  if (isFullIndicator)
    return `${numdays} ${t('general.days')} ${numhours} ${t('general.hours')} ${minutes} ${numseconds} ${t('general.seconds')}`;
  return `${numdays}d ${numhours}h ${numminutes}m ${numseconds}s`;
}

/**
 * Return a string like "5 days" ago from a timestamp
 * @param {timestamp} date UTC timestamp in seconds that has to be compared to the current UTC timestamp
 * @returns
 */
export function timeSince(date) {
  var seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 0) seconds = 0;

  var interval = seconds / 31536000;

  if (interval > 1) {
    interval = Math.floor(interval);
    return interval + (interval === 1 ? ` ${t('general.year')}` : ` ${t('general.years')}`);
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    interval = Math.floor(interval);
    return interval + (interval === 1 ? ` ${t('general.month')}` : ` ${t('general.months')}`);
  }
  interval = seconds / 86400;
  if (interval > 1) {
    interval = Math.floor(interval);
    return interval + (interval === 1 ? ` ${t('general.day')}` : ` ${t('general.days')}`);
  }
  interval = seconds / 3600;
  if (interval > 1) {
    interval = Math.floor(interval);
    return interval + (interval === 1 ? ` ${t('general.hour')}` : ` ${t('general.hours')}`);
  }
  interval = seconds / 60;
  if (interval > 1) {
    interval = Math.floor(interval);
    return interval + (interval === 1 ? ` ${t('general.minute')}` : ` ${t('general.minutes')}`);
  }
  return Math.floor(seconds) + ` ${t('general.seconds')}`;
}

export function timeSinceShort(date) {
  var seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 0) seconds = 0;

  var interval = seconds / 31536000;

  if (interval > 1) {
    interval = Math.floor(interval);
    return `${interval} y.`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    interval = Math.floor(interval);
    return `${interval} mon.`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    interval = Math.floor(interval);
    return `${interval} d.`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    interval = Math.floor(interval);
    return `${interval} h.`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    interval = Math.floor(interval);
    return `${interval} min.`;
  }
  return `${Math.floor(seconds)} sec.`;
}

export const timeStampToMs = timeStamp => {
  if (typeof timeStamp === 'string') {
    const timeStampsArray = timeStamp.split(':').map(item => Number.parseInt(item));
    const msArray = timeStampsArray
      .reverse()
      .map((item, index) => {
        switch (index) {
          case 0: // MS
            return item;
          case 1: // Seconds
            return item * 1000;
          case 2: // Minutes
            return item * 60 * 1000;
          case 3: // Hours
            return item * 60 * 60 * 1000;
          default:
            return 0;
        }
      })
      .reverse();
    return msArray.reduce((a, b) => a + b);
  } else {
    return timeStamp;
  }
};

export const formatSecondsToDuration = seconds => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  const hoursDur = duration.hours > 0 ? `${duration.hours}:` : ``; // If more than 59 minutes, it shown also the hour state
  const minutesDur = duration.hours > 0 && duration.minutes < 10 ? `0${duration.minutes}` : `${duration.minutes}`; // When its 1 hour, but only 3minutes, it shown also the 0 before 3
  let secondsDur = '';
  if (seconds > 0 && duration.seconds === 0) {
    secondsDur = '01';
  } else {
    secondsDur = duration.seconds > 9 ? duration.seconds : `0${duration.seconds}`;
  }
  return `${hoursDur}${minutesDur}:${secondsDur}`;
};

export const elapsedTimeUntilNow = date => {
  const currentDate = new Date();
  const minuteAgo = new Date(currentDate.getTime() - 60000);
  const hourAgo = new Date(currentDate.getTime() - 3600000);
  const dayAgo = new Date(currentDate.getTime() - 86400000);
  const weekAgo = new Date(currentDate.getTime() - 604800000);
  const yearAgo = new Date(currentDate.getTime() - 31536000000);

  const test = new Date(date);

  if (test > minuteAgo) {
    return `${t('general.time.just_now')}`;
  } else if (test > hourAgo) {
    const diff = Math.floor((currentDate - test) / 60000);
    return `${diff} m.`;
  } else if (test > dayAgo) {
    const diff = Math.floor((currentDate - test) / 3600000);
    return `${diff} ${t('general.time.hours')}`;
  } else if (test > weekAgo) {
    const diff = Math.floor((currentDate - test) / 86400000);
    return `${diff} ${t('general.time.days')}`;
  } else if (test > yearAgo) {
    const diff = Math.floor((currentDate - test) / 86400000);
    return `${Math.floor(diff / 7)} w.`;
  } else {
    const diff = Math.floor((currentDate - test) / 31536000000);
    return `${diff} ${t('general.time.years')}`;
  }
};

export const formatToDateString = (utcString, language = 'en-US', includeTime = true) => {
  if (!utcString) {
    return null;
  }

  const currentDate = new Date(utcString);

  const date = currentDate.getDate();
  const monthName = currentDate.toLocaleString(language, { month: 'short' });
  const fullYear = currentDate.getFullYear();

  if (includeTime) {
    const hours = currentDate.getHours();
    const formattedHours = hours < 10 ? `0${hours}` : hours;

    const minutes = currentDate.getMinutes();
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${date} ${monthName} ${fullYear} ${formattedHours}:${formattedMinutes}`;
  } else {
    return `${date} ${monthName} ${fullYear}`;
  }
};

export const convertSeconds = seconds => {
  if (!isFinite(seconds) || isNaN(seconds)) {
    return '00:00';
  }
  if (typeof seconds === 'number') {
    return new Date(seconds * 1000).toISOString().substr(11, 12);
  }
  return seconds;
};

export const timeStringToSeconds = (timeString) => {
  const parts = timeString.split(':').map(Number);

  if (parts.length === 3) {
    // hh:mm:ss
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  } else if (parts.length === 2) {
    // mm:ss
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }

  return 0;
}

export const secondsToTimeString = (currentSeconds) => {
  if (isNaN(currentSeconds)) {
    return '00:00';
  }
  const seconds = Math.round(currentSeconds);
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const formattedHrs = String(hrs).padStart(2, '0');
  const formattedMins = String(mins).padStart(2, '0');
  const formattedSecs = String(secs).padStart(2, '0');

  return hrs > 0
    ? `${formattedHrs}:${formattedMins}:${formattedSecs}`
    : `${formattedMins}:${formattedSecs}`;
}
